import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { CognitoUser } from 'amazon-cognito-identity-js';
import {
  signIn,
  completeNewPassword,
  getCurrentSession,
  getCurrentUserEmail,
  signOut as cognitoSignOut,
  signUp,
} from '../services/auth';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  userEmail: string | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  register: (email: string, password: string) => Promise<LoginResult>;
  setNewPassword: (newPassword: string) => Promise<LoginResult>;
  signOut: () => void;
}

export interface LoginResult {
  success: boolean;
  newPasswordRequired?: boolean;
  error?: string;
}

const AuthContext = createContext<AuthState>({
  isAuthenticated: false,
  isLoading: true,
  userEmail: null,
  login: async () => ({ success: false }),
  register: async () => ({ success: false }),
  setNewPassword: async () => ({ success: false }),
  signOut: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [pendingUser, setPendingUser] = useState<CognitoUser | null>(null);

  // Check for existing session on mount
  useEffect(() => {
    (async () => {
      const session = await getCurrentSession();
      if (session?.isValid()) {
        setIsAuthenticated(true);
        setUserEmail(await getCurrentUserEmail());
      }
      setIsLoading(false);
    })();
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const result = await signIn(email, password);
    if (result.success) {
      setIsAuthenticated(true);
      setUserEmail(email);
      return { success: true };
    }
    if (result.newPasswordRequired && result.cognitoUser) {
      setPendingUser(result.cognitoUser);
      return { success: false, newPasswordRequired: true };
    }
    return { success: false, error: result.error };
  }, []);

  const register = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const result = await signUp(email, password);
    if (result.success) {
      // Auto-confirmed by pre-signup Lambda, so sign them in immediately
      return login(email, password);
    }
    return { success: false, error: result.error };
  }, [login]);

  const setNewPassword = useCallback(async (newPassword: string): Promise<LoginResult> => {
    if (!pendingUser) return { success: false, error: 'No pending password challenge' };
    const result = await completeNewPassword(pendingUser, newPassword);
    if (result.success) {
      setIsAuthenticated(true);
      setUserEmail(await getCurrentUserEmail());
      setPendingUser(null);
      return { success: true };
    }
    return { success: false, error: result.error };
  }, [pendingUser]);

  const handleSignOut = useCallback(() => {
    cognitoSignOut();
    setIsAuthenticated(false);
    setUserEmail(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        userEmail,
        login,
        register,
        setNewPassword,
        signOut: handleSignOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
